/**
 * HTML Block Extractor
 *
 * Parses full HTML documents and extracts translatable content blocks
 * (headings, paragraphs, links, images, etc.) with stable IDs.
 * Also supports reconstructing HTML from translated blocks and
 * syncing blocks when the source HTML changes.
 */

// Tags whose content is directly extractable
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6'])
const RICHTEXT_TAGS = new Set(['P', 'LI', 'BLOCKQUOTE'])
const TEXT_TAGS = new Set(['BUTTON', 'TD', 'TH', 'FIGCAPTION', 'LABEL'])

// Tags to skip entirely (not content, not containers)
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'SVG', 'NOSCRIPT', 'META', 'LINK', 'HEAD', 'FOOTER'])

// Tags that are always structural containers (never extracted directly)
const CONTAINER_TAGS = new Set([
  'DIV', 'SECTION', 'NAV', 'HEADER', 'MAIN', 'ARTICLE',
  'ASIDE', 'FIGURE', 'TABLE', 'TBODY', 'THEAD', 'TFOOT', 'TR',
  'UL', 'OL', 'DL', 'FORM', 'FIELDSET', 'DETAILS', 'SUMMARY'
])

// Inline tags that don't constitute block-level content on their own
const INLINE_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'SMALL', 'SUB', 'SUP',
  'MARK', 'ABBR', 'CODE', 'KBD', 'CITE', 'TIME', 'BR'
])

/**
 * Check if an element has any block-level child elements.
 * Elements with only inline children (text, <b>, <span>, etc.) are
 * considered "leaf content" and can be extracted as a single block.
 */
function hasBlockChildren (el) {
  for (const child of el.children) {
    if (!INLINE_TAGS.has(child.tagName) && child.tagName !== 'A') {
      return true
    }
  }
  return false
}

/**
 * Check if an element has meaningful text content (not just whitespace).
 */
function hasTextContent (el) {
  return el.textContent && el.textContent.trim().length > 0
}

/**
 * Find the text-bearing leaf elements inside a container (used for <summary>,
 * whose title text is typically wrapped in an inline span and sits next to
 * decorative SVG icons). A "text leaf" is an element that has meaningful text
 * but no descendant element that itself carries text — i.e. the innermost
 * wrapper around the visible label. SKIP_TAGS (svg, etc.) are ignored so
 * decorative icons never become editable blocks.
 *
 * Returns leaves in document order. Empty if the container has no nested
 * text-bearing element (e.g. text sits directly on the container).
 */
function findTextLeaves (el) {
  const leaves = []
  for (const candidate of el.querySelectorAll('*')) {
    if (SKIP_TAGS.has(candidate.tagName)) continue
    if (!hasTextContent(candidate)) continue
    let hasTextChild = false
    for (const child of candidate.children) {
      if (!SKIP_TAGS.has(child.tagName) && hasTextContent(child)) {
        hasTextChild = true
        break
      }
    }
    if (!hasTextChild) leaves.push(candidate)
  }
  return leaves
}

/**
 * After removing a child element, walk up the ancestor chain and remove
 * any container that has become empty (no element children, no meaningful
 * text). Stops at body/html or any non-container element.
 */
function removeEmptyAncestors (startParent, doc) {
  let current = startParent
  while (current && current !== doc.body && current !== doc.documentElement) {
    if (!CONTAINER_TAGS.has(current.tagName)) break
    const hasElementChildren = current.children.length > 0
    const hasMeaningfulText = current.textContent && current.textContent.trim().length > 0
    if (hasElementChildren || hasMeaningfulText) break
    const parent = current.parentNode
    if (parent) parent.removeChild(current)
    current = parent
  }
}

/**
 * Walk the DOM tree and collect extractable content blocks.
 * Returns an ordered array of raw block descriptors.
 */
function walkAndExtract (node, options = {}) {
  const skipContentCheck = options.skipContentCheck || false
  const results = []

  for (const child of node.childNodes) {
    // Skip text nodes at this level (we only extract text inside elements)
    if (child.nodeType === Node.TEXT_NODE) continue
    if (child.nodeType !== Node.ELEMENT_NODE) continue

    const tag = child.tagName

    // Skip non-content tags
    if (SKIP_TAGS.has(tag)) continue

    // Headings — always extract as a single block
    if (HEADING_TAGS.has(tag)) {
      if (skipContentCheck || hasTextContent(child)) {
        results.push({ tag: tag.toLowerCase(), type: 'heading', content: child.innerHTML.trim(), element: child })
      }
      continue
    }

    // Rich text elements (p, li, blockquote)
    if (RICHTEXT_TAGS.has(tag)) {
      if (skipContentCheck || hasTextContent(child)) {
        results.push({ tag: tag.toLowerCase(), type: 'richtext', content: child.innerHTML.trim(), element: child })
      }
      continue
    }

    // Links — standalone links (not inside a paragraph, which is handled above via innerHTML)
    if (tag === 'A') {
      if (skipContentCheck || hasTextContent(child)) {
        results.push({
          tag: 'a',
          type: 'link',
          content: child.textContent.trim(),
          href: child.getAttribute('href') || '',
          element: child
        })
      }
      continue
    }

    // Images
    if (tag === 'IMG') {
      const src = child.getAttribute('src') || ''
      if (skipContentCheck || src) {
        results.push({ tag: 'img', type: 'image', src, element: child })
      }
      continue
    }

    // Text-level elements (button, td, th, etc.)
    if (TEXT_TAGS.has(tag)) {
      if (skipContentCheck || hasTextContent(child)) {
        results.push({ tag: tag.toLowerCase(), type: 'text', content: child.innerHTML.trim(), element: child })
      }
      continue
    }

    // Disclosure header (<summary>) — extract only the title text, not the
    // decorative icon/chevron SVGs. The title is usually in a nested span, so
    // extract the text leaves; fall back to the summary itself if its text
    // sits directly on the element.
    if (tag === 'SUMMARY') {
      const leaves = findTextLeaves(child)
      if (leaves.length) {
        for (const leaf of leaves) {
          results.push({ tag: 'summary', type: 'text', content: leaf.innerHTML.trim(), element: leaf })
        }
      } else if (skipContentCheck || hasTextContent(child)) {
        results.push({ tag: 'summary', type: 'text', content: child.innerHTML.trim(), element: child })
      }
      continue
    }

    // Container tags and unknown elements — recurse into children
    if (CONTAINER_TAGS.has(tag) || !INLINE_TAGS.has(tag)) {
      // Check if this is a "leaf container" — a div/span with only text/inline children
      if (!hasBlockChildren(child) && (skipContentCheck || hasTextContent(child))) {
        // Check if container has multiple distinct inline children with text
        const inlineChildrenWithText = Array.from(child.children).filter(
          c => (INLINE_TAGS.has(c.tagName) || c.tagName === 'A' || c.tagName === 'IMG') &&
               (skipContentCheck || hasTextContent(c) || (c.tagName === 'IMG' && c.getAttribute('src')))
        )
        if (child.children.length > 1 && inlineChildrenWithText.length > 1) {
          // Multiple distinct inline elements — extract each separately
          for (const el of child.children) {
            const elTag = el.tagName
            if (elTag === 'A' && (skipContentCheck || hasTextContent(el))) {
              results.push({ tag: 'a', type: 'link', content: el.textContent.trim(), href: el.getAttribute('href') || '', element: el })
            } else if (elTag === 'IMG') {
              const src = el.getAttribute('src') || ''
              if (skipContentCheck || src) {
                results.push({ tag: 'img', type: 'image', src, element: el })
              }
            } else if ((INLINE_TAGS.has(elTag) || TEXT_TAGS.has(elTag)) && (skipContentCheck || hasTextContent(el))) {
              results.push({ tag: elTag.toLowerCase(), type: 'text', content: el.textContent.trim(), element: el })
            }
          }
        } else {
          // Single content block — extract the container's innerHTML
          results.push({ tag: tag.toLowerCase(), type: 'text', content: child.innerHTML.trim(), element: child })
        }
      } else {
        // Structural container — recurse
        results.push(...walkAndExtract(child, options))
      }
      continue
    }
  }

  return results
}

/**
 * Extract content blocks from an HTML string.
 *
 * @param {string} html - Full HTML document string
 * @param {string} lang - Language code for the extracted content (e.g., 'en')
 * @returns {{ blocks: Object, meta: Object }} - blocks: { blockId: { [lang]: content } }, meta: { blockId: { tag, type, order, group? } }
 */
export function extractBlocks (html, lang) {
  if (!html || !html.trim()) {
    return { blocks: {}, meta: {} }
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const rawBlocks = walkAndExtract(doc.body)

  const blocks = {}
  const meta = {}
  let index = 0

  for (const raw of rawBlocks) {
    if (raw.type === 'link') {
      // Links produce two blocks: text + href
      const group = `a_${index}`
      const textId = `${group}_text`
      const hrefId = `${group}_href`

      blocks[textId] = { [lang]: raw.content }
      meta[textId] = { tag: 'a', type: 'text', order: index, group }

      blocks[hrefId] = { [lang]: raw.href }
      meta[hrefId] = { tag: 'a', type: 'url', order: index, group }
    } else if (raw.type === 'image') {
      const id = `img_${index}_src`
      blocks[id] = { [lang]: raw.src }
      meta[id] = { tag: 'img', type: 'image', order: index }
    } else {
      const id = `${raw.tag}_${index}`
      blocks[id] = { [lang]: raw.content }
      meta[id] = { tag: raw.tag, type: raw.type, order: index }
    }

    index++
  }

  return { blocks, meta }
}

/**
 * Reconstruct an HTML string by replacing content in the default-language
 * HTML with translated block values.
 *
 * @param {string} defaultHtml - The default language HTML (used as structural template)
 * @param {Object} blocks - { blockId: { lang: content } }
 * @param {Object} meta - { blockId: { tag, type, order, group? } }
 * @param {string} targetLang - Language to render
 * @param {string} defaultLang - Fallback language
 * @returns {string} - Reconstructed HTML string
 */
export function reconstructHtml (defaultHtml, blocks, meta, targetLang, defaultLang) {
  if (!defaultHtml || !blocks || !meta) return defaultHtml || ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(defaultHtml, 'text/html')
  const rawBlocks = walkAndExtract(doc.body)

  // Build ordered list of block IDs by their order value
  const orderedIds = Object.keys(meta).sort((a, b) => meta[a].order - meta[b].order)

  // Group block IDs by order (links have 2 IDs for the same order)
  const blocksByOrder = {}
  for (const id of orderedIds) {
    const order = meta[id].order
    if (!blocksByOrder[order]) blocksByOrder[order] = []
    blocksByOrder[order].push(id)
  }

  // Walk extracted elements and apply translated content
  for (let i = 0; i < rawBlocks.length; i++) {
    const raw = rawBlocks[i]
    const idsForOrder = blocksByOrder[i]
    if (!idsForOrder) continue

    for (const blockId of idsForOrder) {
      const blockData = blocks[blockId]
      if (!blockData) continue

      const value = blockData[targetLang] || blockData[defaultLang] || ''
      const blockMeta = meta[blockId]

      if (raw.type === 'link' && blockMeta.type === 'text') {
        raw.element.textContent = value
      } else if (raw.type === 'link' && blockMeta.type === 'url') {
        raw.element.setAttribute('href', value)
      } else if (raw.type === 'image' && blockMeta.type === 'image') {
        raw.element.setAttribute('src', value)
      } else if (blockMeta.type === 'heading' || blockMeta.type === 'richtext' || blockMeta.type === 'text') {
        raw.element.innerHTML = value
      }
    }
  }

  // Serialize back — preserve fragment vs full document format
  const isFullDocument = defaultHtml.toLowerCase().includes('<html')
  if (isFullDocument) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML
  }
  return doc.body.innerHTML
}

/**
 * Sync blocks when the source HTML changes (e.g., after code edit or AI generation).
 * Preserves translations for blocks whose content hasn't changed.
 *
 * @param {string} newHtml - The updated HTML string
 * @param {Object} existingBlocks - Current blocks (with translations)
 * @param {Object} existingMeta - Current block metadata
 * @param {string} defaultLang - Default language code
 * @returns {{ blocks: Object, meta: Object }}
 */
export function syncBlocks (newHtml, existingBlocks, existingMeta, defaultLang) {
  const { blocks: freshBlocks, meta: freshMeta } = extractBlocks(newHtml, defaultLang)

  if (!existingBlocks || !Object.keys(existingBlocks).length) {
    return { blocks: freshBlocks, meta: freshMeta }
  }

  // Build a lookup of existing blocks by their default language content (array for duplicates)
  const existingByContent = {}
  for (const [id, blockData] of Object.entries(existingBlocks)) {
    const content = blockData[defaultLang] || ''
    if (content) {
      if (!existingByContent[content]) existingByContent[content] = []
      existingByContent[content].push({ id, data: blockData })
    }
  }

  // Build a lookup of existing blocks by their order position
  const existingByOrder = {}
  if (existingMeta) {
    for (const [id, m] of Object.entries(existingMeta)) {
      existingByOrder[m.order] = { id, data: existingBlocks[id] }
    }
  }

  // Track consumed existing blocks to prevent double-matching
  const consumed = new Set()
  const mergedBlocks = {}

  for (const [newId, newBlockData] of Object.entries(freshBlocks)) {
    const newContent = newBlockData[defaultLang] || ''
    const newOrder = freshMeta[newId] ? freshMeta[newId].order : -1

    // Try to find a matching existing block:
    // 1. Same position AND same content — perfect match, keep all translations
    const samePosition = existingByOrder[newOrder]
    if (samePosition && !consumed.has(samePosition.id) && samePosition.data && samePosition.data[defaultLang] === newContent) {
      mergedBlocks[newId] = { ...samePosition.data, [defaultLang]: newContent }
      consumed.add(samePosition.id)
      continue
    }

    // 2. Same content at different position — content moved, keep translations
    const contentMatches = existingByContent[newContent] || []
    const unconsumedMatch = contentMatches.find(m => !consumed.has(m.id))
    if (unconsumedMatch) {
      mergedBlocks[newId] = { ...unconsumedMatch.data, [defaultLang]: newContent }
      consumed.add(unconsumedMatch.id)
      continue
    }

    // 3. No content match — genuinely new or edited content, start fresh
    //    (We intentionally do NOT inherit translations from the block at the
    //    same position, as the content has changed and old translations would
    //    be wrong. The user needs to re-translate the changed text.)
    mergedBlocks[newId] = newBlockData
  }

  return { blocks: mergedBlocks, meta: freshMeta }
}

/**
 * Get an ordered array of block entries for rendering in the simplified view.
 *
 * @param {Object} blocks - { blockId: { lang: content } }
 * @param {Object} meta - { blockId: { tag, type, order, group? } }
 * @returns {Array} - Sorted array of { id, ...meta, content: blocks[id] }
 */
/**
 * Highlight styles and script injected into the iframe HTML.
 * The script listens for postMessage events to highlight/unhighlight elements.
 */
const HIGHLIGHT_STYLE = `
[data-block-highlight] {
  outline: 2px solid #1976d2 !important;
  outline-offset: 2px;
  background-color: rgba(25, 118, 210, 0.08) !important;
  transition: outline-color 0.15s, background-color 0.15s;
}
`

const HIGHLIGHT_SCRIPT = `
window.addEventListener('message', function(event) {
  var data = event.data;
  if (!data || !data.type) return;

  // Clear all highlights
  var highlighted = document.querySelectorAll('[data-block-highlight]');
  for (var i = 0; i < highlighted.length; i++) {
    highlighted[i].removeAttribute('data-block-highlight');
  }

  if (data.type === 'highlight-block' && data.blockId) {
    var el = document.querySelector('[data-block-id="' + data.blockId + '"]');
    if (el) {
      el.setAttribute('data-block-highlight', '');
      if (data.scroll !== false) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
});

// Outbound: relay hover/click back to the parent so the simplified view can react.
function __stcClosestBlockId (target) {
  if (!target || !target.closest) return null;
  var el = target.closest('[data-block-id]');
  return el ? el.getAttribute('data-block-id') : null;
}
var __stcLastHover = null;
document.addEventListener('mouseover', function (e) {
  var id = __stcClosestBlockId(e.target);
  if (id === __stcLastHover) return;
  __stcLastHover = id;
  window.parent.postMessage({ type: 'preview-hover', blockId: id }, '*');
});
document.addEventListener('mouseleave', function () {
  if (__stcLastHover === null) return;
  __stcLastHover = null;
  window.parent.postMessage({ type: 'preview-hover', blockId: null }, '*');
}, true);
document.addEventListener('click', function (e) {
  // Let native disclosure widgets toggle: clicking a <summary> must still
  // open/close its parent <details>, so don't neutralise that default action.
  var isSummary = e.target.closest && e.target.closest('summary');
  if (!isSummary) {
    // Neutralise default actions (link navigation, form submission, button handlers)
    e.preventDefault();
    e.stopPropagation();
  }
  var id = __stcClosestBlockId(e.target);
  if (id) {
    window.parent.postMessage({ type: 'preview-click', blockId: id }, '*');
  }
}, true);
`

/**
 * Annotate HTML for preview by injecting data-block-id attributes on
 * extractable elements and adding a highlight script + styles.
 * The stored HTML stays clean — this is for iframe rendering only.
 *
 * @param {string} html - The HTML string to annotate
 * @param {Object} meta - Block metadata from extractBlocks ({ blockId: { tag, type, order, group? } })
 * @returns {string} - Annotated HTML string
 */
export function annotateHtmlForPreview (html, meta) {
  if (!html || !html.trim() || !meta || !Object.keys(meta).length) return html || ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const rawBlocks = walkAndExtract(doc.body)

  // Build ordered block IDs by order
  const orderedIds = Object.keys(meta).sort((a, b) => meta[a].order - meta[b].order)
  const blocksByOrder = {}
  for (const id of orderedIds) {
    const order = meta[id].order
    if (!blocksByOrder[order]) blocksByOrder[order] = []
    blocksByOrder[order].push(id)
  }

  // Inject data-block-id attributes
  for (let i = 0; i < rawBlocks.length; i++) {
    const raw = rawBlocks[i]
    const idsForOrder = blocksByOrder[i]
    if (!idsForOrder || !idsForOrder.length) continue

    // Use the first block ID for the element (links/images have multiple IDs for one element)
    const primaryId = idsForOrder[0]
    raw.element.setAttribute('data-block-id', primaryId)
  }

  // Inject highlight styles
  const styleEl = doc.createElement('style')
  styleEl.textContent = HIGHLIGHT_STYLE
  doc.head.appendChild(styleEl)

  // Inject highlight script
  const scriptEl = doc.createElement('script')
  scriptEl.textContent = HIGHLIGHT_SCRIPT
  doc.body.appendChild(scriptEl)

  // Serialize
  const isFullDocument = html.toLowerCase().includes('<html')
  if (isFullDocument) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML
  }
  return doc.body.innerHTML
}

/**
 * Remove the DOM element corresponding to a block from HTML.
 * For grouped blocks (link text + href), removes the parent element at that order.
 *
 * @param {string} html - The HTML string
 * @param {Object} meta - Block metadata
 * @param {string} blockId - The block ID to remove
 * @returns {string} - Updated HTML with element removed
 */
export function removeBlockFromHtml (html, meta, blockId) {
  if (!html || !meta || !meta[blockId]) return html

  const order = meta[blockId].order
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const rawBlocks = walkAndExtract(doc.body)

  if (order < 0 || order >= rawBlocks.length) return html

  const element = rawBlocks[order].element
  if (element && element.parentNode) {
    // A <summary> is the header of a <details> disclosure panel. Deleting the
    // section header should remove the entire panel (header + body), not just
    // the title text, so target the enclosing <details>.
    const summary = element.closest ? element.closest('summary') : null
    const target = (summary && summary.closest('details')) || element
    const parent = target.parentNode
    if (parent) {
      parent.removeChild(target)
      removeEmptyAncestors(parent, doc)
    }
  }

  const isFullDocument = html.toLowerCase().includes('<html')
  if (isFullDocument) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML
  }
  return doc.body.innerHTML
}

export function getOrderedBlocks (blocks, meta) {
  if (!blocks || !meta) return []

  return Object.keys(meta)
    .sort((a, b) => meta[a].order - meta[b].order)
    .map(id => ({
      id,
      ...meta[id],
      content: blocks[id] || {}
    }))
}
